import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { supabase, signInWithGoogle as supabaseSignInWithGoogle, signInWithLinkedIn as supabaseSignInWithLinkedIn, getRedirectUrl } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { addCredits } from '@/services/creditService';
import { createClient } from '@supabase/supabase-js';
import Stripe from 'stripe';
import { trackEvent, setUserId } from '@/utils/analytics';
import { Profile } from '@/types/profile';
import { createHubspotContact, updateHubspotContact as updateHubspotContactService } from '@/services/hubspotService';
import { isPersonalEmail } from '@/utils/emailValidator';

// Create a Supabase client with the service role key for admin operations
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SERVICE_ROLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Initialize service role client for admin operations
let serviceRoleClient;
try {
  serviceRoleClient = createClient(
    SUPABASE_URL,
    SERVICE_ROLE_KEY || ''
  );
} catch (err) {
  //console.error("Failed to initialize service role client:", err);
  // Fallback to regular supabase client
  serviceRoleClient = supabase;
}

// Improve typing for profile
interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  setProfile: (profile: Profile | null) => void;
  signUp: (email: string, password: string, metadata: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: () => Promise<{ provider: string; url: string | null; } | undefined>;
  signInWithMicrosoft: () => Promise<{ provider: string; url: string; } | undefined>;
  signInWithLinkedIn: () => Promise<{ provider: string; url: string | null; } | undefined>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  updatePassword: (password: string) => Promise<any>;
  loading: boolean;
  refreshSession: () => Promise<void>;
  isAdmin: () => boolean;
  isContentEditor: () => boolean;
  isCandidate: () => boolean;
  isRecruiter: () => boolean;
  isRecruiterVerified: () => boolean;
  assignBasicPlan: (userId: string, email: string, metadata?: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchingProfileRef = useRef(false);
  const dataLoadedRef = useRef(false);
  const navigate = useNavigate();

  const createProfile = useCallback(async (userId: string, metadata: any = {}) => {
    try {
      // First check if profile already exists
      const { data: existingProfile, error: profileCheckError } = await serviceRoleClient
        .from('profiles')
        .select('id, organization_id, first_name, last_name, user_role')
        .eq('id', userId)
        .single();

      if (profileCheckError && profileCheckError.code !== 'PGRST116') {
        // If error is not "no rows returned", it's a real error
        throw profileCheckError;
      }

      // If profile exists, check if it has an organization ID
      if (existingProfile) {
        // Check if first_name or last_name are missing and update if needed
        if (!existingProfile.first_name || !existingProfile.last_name) {
          // Ensure first_name and last_name are properly extracted from metadata
          const first_name = metadata.first_name || 
                           metadata.firstName || 
                           metadata.name?.split(' ')[0] || 
                           metadata.full_name?.split(' ')[0] || '';
                           
          const last_name = metadata.last_name || 
                          metadata.lastName || 
                          metadata.name?.split(' ').slice(1).join(' ') || 
                          metadata.full_name?.split(' ').slice(1).join(' ') || '';
          
          // Update the profile with names
          const { error: updateError } = await serviceRoleClient
            .from('profiles')
            .update({ 
              first_name,
              last_name,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);
            
          if (updateError) {
            //console.error('Error updating profile names:', updateError);
          }
        }
        
        // Check if user_role is missing and update it with default 'recruiter' if needed
        if (!existingProfile.user_role) {
          const { error: roleUpdateError } = await serviceRoleClient
            .from('profiles')
            .update({ 
              user_role: 'recruiter', // Default role
              updated_at: new Date().toISOString()
            })
            .eq('id', userId);
            
          if (roleUpdateError) {
            //console.error('Error updating profile role:', roleUpdateError);
          }
        }
        
        // Check if organization_id exists, if not, create organization and update profile
        if (!existingProfile.organization_id) {
          // Create a new organization with default name
          const { data: newOrg, error: orgError } = await serviceRoleClient
            .from('organizations')
            .insert([{
              name: '', // Empty name to trigger onboarding
              owner_id: userId
            }])
            .select()
            .single();

          if (orgError) {
            throw orgError;
          }

          if (!newOrg) {
            throw new Error('Failed to create organization: No organization returned');
          }
          
          // Update the profile with the new organization ID
          const { data: updatedProfile, error: updateError } = await serviceRoleClient
            .from('profiles')
            .update({ 
              organization_id: newOrg.id,
              updated_at: new Date().toISOString()
            })
            .eq('id', userId)
            .select()
            .single();
            
            if (updateError) {
              throw updateError;
            }
          
          return updatedProfile;
        }
        
        return existingProfile;
      }
      
      // Profile doesn't exist, create both organization and profile
      
      // Ensure first_name and last_name are properly extracted from metadata
      const first_name = metadata.first_name || 
                       metadata.firstName || 
                       metadata.name?.split(' ')[0] || 
                       metadata.full_name?.split(' ')[0] || '';
                       
      const last_name = metadata.last_name || 
                      metadata.lastName || 
                      metadata.name?.split(' ').slice(1).join(' ') || 
                      metadata.full_name?.split(' ').slice(1).join(' ') || '';
                      
      const avatar_url = metadata.avatar_url || metadata.picture || '';

      // Create a new organization with default name
      const { data: newOrg, error: orgError } = await serviceRoleClient
        .from('organizations')
        .insert([{
          name: '', // Empty name to trigger onboarding
          owner_id: userId
        }])
        .select()
        .single();

      if (orgError) {
        throw orgError;
      }

      if (!newOrg) {
        throw new Error('Failed to create organization: No organization returned');
      }

      // Check if we have a specific user_role in metadata (could be set during signup)
      const user_role = metadata.user_role || 'recruiter'; // Default to recruiter if not specified

      // Create a new profile
      const { data: createdProfile, error } = await serviceRoleClient
        .from('profiles')
        .insert([{
          id: userId,
          first_name,
          last_name,
          avatar_url,
          company: metadata.company || '',
          role: metadata.role || '',
          user_role,
          organization_id: newOrg.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }])
        .select()
        .single();

      if (error) {
        // If error is duplicate key, profile was created in a race condition
        if (error.code === '23505') {
          return await createProfile(userId, metadata);
        }
        throw error;
      }
      
      return createdProfile;
    } catch (error: any) {
      // Only throw error if it's not a duplicate key error
      if (error.code !== '23505') {
        throw error;
      }
      
      return await createProfile(userId, metadata);
    }
  }, []);

  // Function to update user information in Hubspot
  const updateHubspotContact = useCallback(async (userId: string, firstName: string, email: string, company: string) => {
    try {
      // Call our hubspot service instead of direct API call
      const response = await createHubspotContact(email, firstName, undefined, company, userId);
      
      if (!response.success) {
        return null;
      }

      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  // Function to assign the BASIC plan to a new user
  const assignBasicPlan = useCallback(async (userId: string, email: string, metadata: any = {}) => {
    try {
      // Check if user already has a subscription - modified to handle multiple results
      const { data: existingSubscriptions, error: checkError } = await serviceRoleClient
        .from('subscriptions')
        .select('id')
        .eq('user_id', userId);
      
      if (checkError) {
        return;
      }

      if (existingSubscriptions && existingSubscriptions.length > 0) {
        return;
      }

      // Get the Basic plan using UUID
      const { data: basicPlan, error: basicPlanError } = await serviceRoleClient
        .from('plans')
        .select('id, credits, name, stripe_price_id')
        .eq('id', '55b573b5-15cd-4c9d-a484-b7eb18403455')
        .single();
        
      if (basicPlanError) {
        return;
      }
      
      if (!basicPlan) {
        return;
      }

      // Get user's organization
      const { data: profile, error: profileError } = await serviceRoleClient
        .from('profiles')
        .select('organization_id, first_name, last_name')
        .eq('id', userId)
        .single();

      if (profileError) {
        return;
      }
      
      if (!profile?.organization_id) {
        return;
      }
      
      // Create a subscription record for the basic plan
      const { error: subscriptionError } = await serviceRoleClient
        .from('subscriptions')
        .insert([{
          user_id: userId,
          organization_id: profile.organization_id,
          plan_id: basicPlan.id,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          current_period_start: new Date().toISOString(),
          current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
        }]);
      
      if (subscriptionError) {
        return;
      }

      try {
        // Add the basic plan credits to the organization
        const { error: creditsError } = await serviceRoleClient.rpc('add_organization_credits', {
          p_organization_id: profile.organization_id,
          p_amount: basicPlan.credits,
          p_description: `Initial ${basicPlan.name} plan credits`,
          p_transaction_type: 'subscription'
        });
        
        if (creditsError) {
          throw creditsError;
        }
      } catch (rpcError) {
        try {
          // Fallback: Insert directly into organization_credits table
          const { error: insertError } = await serviceRoleClient
            .from('organization_credits')
            .insert([{
              organization_id: profile.organization_id,
              amount: basicPlan.credits,
              description: `Initial ${basicPlan.name} plan credits`,
              transaction_type: 'subscription',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }]);
            
          if (insertError) {
            return;
          }
          
          // Update organization balance
          const { error: updateError } = await serviceRoleClient
            .from('organizations')
            .update({ 
              credit_balance: basicPlan.credits,
              updated_at: new Date().toISOString() 
            })
            .eq('id', profile.organization_id);
            
          if (updateError) {
            return;
          }
        } catch (fallbackError) {
          return;
        }
      }
    } catch (error) {
    }
  }, []);

  const fetchProfile = useCallback(async (userId: string, metadata: any = {}) => {
    try {
      // Get the user's profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*, organizations(*)')
        .eq('id', userId)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          // Profile doesn't exist, create it
          const newProfile = await createProfile(userId, metadata);
          
          // Fetch the full profile with organizations data
          const { data: completeProfile, error: fetchError } = await supabase
            .from('profiles')
            .select('*, organizations(*)')
            .eq('id', userId)
            .single();
            
          if (fetchError) {
            setProfile(newProfile);
            return newProfile;
          }
          
          setProfile(completeProfile);
          return completeProfile;
        }
        throw profileError;
      }

      // Check if the profile has an organization_id
      if (!profileData.organization_id) {
        const updatedProfile = await createProfile(userId, metadata);
        
        // Fetch the full profile with organizations data
        const { data: completeProfile, error: fetchError } = await supabase
          .from('profiles')
          .select('*, organizations(*)')
          .eq('id', userId)
          .single();
          
        if (fetchError) {
          setProfile(updatedProfile);
          return updatedProfile;
        }
        
        setProfile(completeProfile);
        return completeProfile;
      }

      setProfile(profileData);
      return profileData;
    } catch (error) {
      toast.error('Failed to fetch profile');
      throw error;
    }
  }, [createProfile]);

  const signInWithGoogle = useCallback(async () => {
    try {
      // Redirect to Google OAuth
      const result = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getRedirectUrl(),
          scopes: 'email profile',
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline'
          }
        }
      });

      if (result.error) {
        throw result.error;
      }
      
      // Note: We don't need to explicitly call updateHubspotContact here 
      // because the OAuth callback will handle it in the AuthCallbackPage
      
      return result.data;
    } catch (error: any) {
      toast.error(error.message || 'Error signing in with Google');
      throw error;
    }
  }, []);

  const signInWithLinkedIn = useCallback(async () => {
    try {
      const result = await supabaseSignInWithLinkedIn();
      return result;
    } catch (error: any) {
      toast.error(error.message || 'Error signing in with LinkedIn');
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      // Track sign-out event before signing out
      if (user?.id) {
        trackEvent('user_auth', 'sign_out', user.app_metadata?.provider || 'email');
      }
      
      // Clear the user ID in analytics
      setUserId('');
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setProfile(null);
      setSession(null);
      
      window.localStorage.removeItem('roundone.auth.token');
      navigate('/', { replace: true });
    } catch (error: any) {
      throw error;
    }
  }, [navigate, user]);

  const refreshSession = useCallback(async () => {
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (currentSession) {
        const { data: { session: refreshedSession }, error: refreshError } = 
          await supabase.auth.refreshSession();
        
        if (refreshError) throw refreshError;
        
        if (refreshedSession) {
          setSession(refreshedSession);
          setUser(refreshedSession.user);
          await fetchProfile(refreshedSession.user.id, refreshedSession.user.user_metadata);
        }
      } else {
        // If no session exists, sign out
        await signOut();
      }
    } catch (error: any) {
      toast.error('Session expired. Please log in again.');
      await signOut();
    }
  }, [fetchProfile, signOut]);

  // Add functions to check user roles
  const isCandidate = useCallback(() => {
    return false; // Always return false since we're removing candidate functionality
  }, []);

  const isRecruiter = useCallback(() => {
    return true; // Always return true since we're making all users recruiters
  }, []);

  const isAdminUser = useCallback(() => {
    return !!profile?.is_admin;
  }, [profile]);

  const isContentEditorUser = useCallback(() => {
    return !!profile?.is_content_editor;
  }, [profile]);

  // Check if a recruiter's email is verified
  const isRecruiterVerified = useCallback(() => {
    // If not a recruiter, this doesn't apply
    if (!isRecruiter()) {
      return true;
    }
    
    // A recruiter is verified ONLY if they have a verified_at timestamp
    if (profile?.verified_at) {
      return true;
    }
    
    // If marked as needing manual verification, they're definitely not verified
    if (profile?.needs_manual_verification === true) {
      return false;
    }
    
    // Having a company_email field set is not enough - it must be verified
    if (profile?.company_email && !profile?.verified_at) {
      return false;
    }
    
    // Check if the user's login email is a company email (not personal)
    // This is a fallback for users who registered directly with a company email
    if (user?.email && profile?.user_role === 'recruiter') {
      const isCompanyEmailDomain = !isPersonalEmail(user.email);
      
      // Even with company domain, we require explicit verification now
      return false;
    }
    
    // If nothing above applies and they're a recruiter, they're not verified
    return false;
  }, [profile, user, isRecruiter]);

  const value = useMemo(() => ({
    session,
    user,
    profile,
    setProfile,
    signUp: async (email: string, password: string, metadata: any) => {
      try {
        setLoading(true);
        
        // Proceed with signup
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
            data: metadata
          }
        });
        
        if (error) throw error;
        
        if (!data?.user) {
          throw new Error('No user returned from signup');
        }
        
        // Ensure metadata has the correct format for createProfile
        const profileMetadata = {
          ...metadata,
          email: email
        };
        
        // Create profile (this is necessary even though we'll create it on auth state change
        // because we need it for the rest of the signup flow)
        await createProfile(data.user.id, profileMetadata);
        
        return data;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    signIn: async (email: string, password: string) => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        // Remove immediate navigation - let onAuthStateChange handle it
        return data;
      } catch (error: any) {
        toast.error(error.message || 'Error signing in');
        throw error;
      }
    },
    signInWithGoogle,
    signInWithMicrosoft: async () => {
      try {
        const result = await supabase.auth.signInWithOAuth({
          provider: 'azure',
          options: {
            redirectTo: getRedirectUrl(),
            scopes: 'email profile openid User.Read',
            queryParams: {
              prompt: 'select_account',
              response_type: 'code',
              tenant: 'common'
            }
          }
        });

        if (result.error) {
          throw result.error;
        }
        
        // Note: We don't need to explicitly call updateHubspotContact here 
        // because the OAuth callback will handle it in the AuthCallbackPage
        
        return result.data;
      } catch (error: any) {
        toast.error(error.message || 'Error signing in with Microsoft');
        throw error;
      }
    },
    signInWithLinkedIn,
    signOut,
    resetPassword: async (email: string) => {
      try {
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`,
        });

        if (error) throw error;
        return data;
      } catch (error: any) {
        toast.error(error.message || 'Error resetting password');
        throw error;
      }
    },
    updatePassword: async (password: string) => {
      try {
        const { data, error } = await supabase.auth.updateUser({
          password,
        });

        if (error) throw error;
        return data;
      } catch (error: any) {
        toast.error(error.message || 'Error updating password');
        throw error;
      }
    },
    loading,
    refreshSession,
    isAdmin: isAdminUser,
    isContentEditor: isContentEditorUser,
    isCandidate,
    isRecruiter,
    isRecruiterVerified,
    assignBasicPlan
  }), [
    session, 
    user, 
    profile, 
    createProfile, 
    signInWithGoogle, 
    signInWithLinkedIn, 
    signOut, 
    loading, 
    refreshSession, 
    isAdminUser, 
    isContentEditorUser, 
    isCandidate, 
    isRecruiter,
    isRecruiterVerified,
    assignBasicPlan
  ]);

  useEffect(() => {
    let mounted = true;
    let authSubscription: { data: { subscription: { unsubscribe: () => void } } } | null = null;

    const initializeAuth = async () => {
      try {
        // Skip initialization if data is already loaded
        if (dataLoadedRef.current && user && session) {
          if (mounted) setLoading(false);
          return;
        }

        // Initial session check
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          if (mounted) setLoading(false);
          return;
        }
        
        if (mounted) {
          if (initialSession) {
            setSession(initialSession);
            setUser(initialSession.user);
            try {
              // Process user metadata to ensure we have first/last name
              const userMetadata = processUserMetadata(initialSession.user.user_metadata);
              
              const fetchedProfile = await fetchProfile(initialSession.user.id, userMetadata);
              dataLoadedRef.current = true;
            } catch (profileError) {
            }
          }
          setLoading(false);
        }

        // Set up auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, currentSession) => {
            if (!mounted) return;

            if (event === 'TOKEN_REFRESHED') {
              if (currentSession) {
                setSession(currentSession);
                setUser(currentSession.user);
                try {
                  // Process user metadata to ensure we have first/last name
                  const userMetadata = processUserMetadata(currentSession.user.user_metadata);
                  await fetchProfile(currentSession.user.id, userMetadata);
                } catch (profileError) {
                }
              }
            } else if (event === 'SIGNED_IN') {
              if (currentSession?.user) {
                setSession(currentSession);
                setUser(currentSession.user);
                
                // Set the user ID for Google Analytics
                setUserId(currentSession.user.id);
                
                // Process user metadata to ensure we have first/last name
                const userMetadata = processUserMetadata(currentSession.user.user_metadata);
                
                if (!fetchingProfileRef.current) {
                  fetchingProfileRef.current = true;
                  try {
                    const fetchedProfile = await fetchProfile(currentSession.user.id, userMetadata);
                    
                    // Track sign-in event
                    trackEvent('user_auth', 'sign_in', currentSession.user.app_metadata?.provider || 'email');
                    
                    // Check if the user has completed onboarding
                    if (fetchedProfile?.organizations?.name) {
                      navigate('/dashboard');
                    } else {
                      // Redirect to onboarding if organization name is missing
                      navigate('/onboarding');
                    }
                  } catch (profileError) {
                    navigate('/dashboard');
                  } finally {
                    fetchingProfileRef.current = false;
                  }
                } else {
                  navigate('/dashboard');
                }
              }
            } else if (event === 'SIGNED_OUT') {
              // Track sign-out event before clearing user data
              if (user?.id) {
                trackEvent('user_auth', 'sign_out', user.app_metadata?.provider || 'email');
              }
              
              setProfile(null);
              setUser(null);
              setSession(null);
              window.localStorage.removeItem('roundone.auth.token');
              navigate('/', { replace: true });
            }
            
            setLoading(false);
          }
        );
        
        authSubscription = { data: { subscription } };
      } catch (error) {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Only refresh session if we have a current session but don't reload all data
        if (session && user) {
          supabase.auth.refreshSession();
        } else if (!dataLoadedRef.current) {
          initializeAuth();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    initializeAuth();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.data.subscription.unsubscribe();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, fetchProfile]);

  // Process user metadata to ensure we have consistent format
  const processUserMetadata = (metadata: any = {}): any => {
    const processedMetadata = { ...metadata };
    
    // Extract name parts if full_name exists but first/last don't
    if (processedMetadata.full_name && 
        (!processedMetadata.first_name && !processedMetadata.firstName)) {
      const nameParts = processedMetadata.full_name.split(' ');
      processedMetadata.first_name = nameParts[0] || '';
      processedMetadata.last_name = nameParts.slice(1).join(' ') || '';
    }
    
    // Extract name parts if name exists but first/last don't
    if (processedMetadata.name && 
        (!processedMetadata.first_name && !processedMetadata.firstName)) {
      const nameParts = processedMetadata.name.split(' ');
      processedMetadata.first_name = nameParts[0] || '';
      processedMetadata.last_name = nameParts.slice(1).join(' ') || '';
    }
    
    // Ensure both formats are available (first_name and firstName)
    if (processedMetadata.first_name && !processedMetadata.firstName) {
      processedMetadata.firstName = processedMetadata.first_name;
    } else if (processedMetadata.firstName && !processedMetadata.first_name) {
      processedMetadata.first_name = processedMetadata.firstName;
    }
    
    // Ensure both formats are available (last_name and lastName)
    if (processedMetadata.last_name && !processedMetadata.lastName) {
      processedMetadata.lastName = processedMetadata.last_name;
    } else if (processedMetadata.lastName && !processedMetadata.last_name) {
      processedMetadata.last_name = processedMetadata.lastName;
    }
    
    // Ensure avatar_url is set if picture exists
    if (!processedMetadata.avatar_url && processedMetadata.picture) {
      processedMetadata.avatar_url = processedMetadata.picture;
    }
    
    return processedMetadata;
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
